// LiveConnectForm — v2-styled `moment serve` connection controls embedded in
// the landing's live-bridge card (replaces the old UploadDrawer's
// LiveConnectionPanel). Dispatches the same wsConnect/wsDisconnect intents;
// websocketSaga owns the connection and, on a successful initial load from
// the landing screen, navigates to the portfolio ledger.

import { useCallback, useState, type KeyboardEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectWebSocketState, wsConfigUpdated } from '../../store/slices/scenarioSourceSlice.ts';
import { wsConnect, wsDisconnect } from '../../store/sagas/websocketSaga.ts';
import { WS_DEFAULT_HOST, WS_DEFAULT_PORT } from '../../types/websocket.ts';

const STATUS_LABELS: Record<string, string> = {
  connected: 'CONNECTED',
  connecting: 'CONNECTING…',
  error: 'ERROR',
  disconnected: 'DISCONNECTED',
};

const STATUS_COLORS: Record<string, string> = {
  connected: 'var(--green)',
  connecting: '#fbbf24',
  error: 'var(--red)',
  disconnected: 'var(--text-muted)',
};

export default function LiveConnectForm() {
  const dispatch = useDispatch();
  const wsState = useSelector(selectWebSocketState);
  const { connectionStatus, specFile, errorMessage } = wsState;

  const [host, setHost] = useState(wsState.host || WS_DEFAULT_HOST);
  const [port, setPort] = useState(String(wsState.port || WS_DEFAULT_PORT));
  const [secure, setSecure] = useState(wsState.secure ?? false);

  const isConnected = connectionStatus === 'connected';
  const isConnecting = connectionStatus === 'connecting';
  const isError = connectionStatus === 'error';
  const isNonLocalhost = host !== 'localhost' && host !== '127.0.0.1';

  const handleConnect = useCallback(() => {
    const trimmedHost = host.trim();
    if (!trimmedHost) return;
    const portNum = parseInt(port, 10);
    if (isNaN(portNum) || portNum < 1 || portNum > 65535) return;
    dispatch(wsConfigUpdated({ host: trimmedHost, port: portNum, secure }));
    dispatch(wsConnect({ host: trimmedHost, port: portNum, secure }));
  }, [dispatch, host, port, secure]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !isConnected && !isConnecting) handleConnect();
    },
    [handleConnect, isConnected, isConnecting],
  );

  return (
    <div className="fv2-live-form" data-screen="live-connect">
      <div className="fv2-live-form-status">
        <span style={{ color: STATUS_COLORS[connectionStatus] }}>●</span>
        <span style={{ color: STATUS_COLORS[connectionStatus] }}>
          {STATUS_LABELS[connectionStatus]}
        </span>
        {isConnected && specFile && (
          <span className="fv2-live-form-spec" title={specFile}>
            {specFile.split('/').pop()}
          </span>
        )}
      </div>

      {!isConnected && (
        <div className="fv2-live-form-inputs">
          <button
            type="button"
            className={`fv2-live-proto${secure ? ' fv2-live-proto-on' : ''}`}
            onClick={() => setSecure((s) => !s)}
            disabled={isConnecting}
            title={secure ? 'Using wss:// (secure)' : 'Using ws:// (plain)'}
          >
            {secure ? 'wss' : 'ws'}
          </button>
          <input
            type="text"
            className="fv2-live-input fv2-live-host"
            placeholder="host"
            aria-label="Bridge host"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isConnecting}
          />
          <input
            type="text"
            className="fv2-live-input fv2-live-port"
            placeholder="port"
            aria-label="Bridge port"
            value={port}
            onChange={(e) => setPort(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isConnecting}
          />
        </div>
      )}

      {isNonLocalhost && !isConnected && (
        <div className="fv2-card-warnings">
          Connecting to a non-localhost host — ensure you trust this network.
        </div>
      )}

      {isError && errorMessage && (
        <div className="fv2-card-error" role="alert">
          {errorMessage}
        </div>
      )}

      <div className="fv2-live-form-actions">
        {!isConnected && !isConnecting && (
          <button type="button" className="fv2-card-btn fv2-btn-green" onClick={handleConnect}>
            {isError ? 'RETRY' : 'CONNECT TO moment serve'}
          </button>
        )}
        {isConnecting && (
          <button
            type="button"
            className="fv2-card-btn fv2-btn-outline"
            onClick={() => dispatch(wsDisconnect())}
          >
            CANCEL
          </button>
        )}
        {(isConnected || isError) && (
          <button
            type="button"
            className="fv2-card-btn fv2-btn-outline"
            onClick={() => dispatch(wsDisconnect())}
          >
            DISCONNECT
          </button>
        )}
      </div>
    </div>
  );
}
